import React, { useState } from "react";
import { X } from "lucide-react";
import { hqApi } from "@/hq/hqApi";
import { TERRITORY_OPTIONS, territoryLabel } from "@/hq/territory";
import { toast } from "sonner";

// Phase 3I.4P.3 — Bulk Set Territory modal. Hard cap 25 matches the
// existing selection ceiling; validated server-side too.
export default function BulkTerritoryModal({ open, businessIds, onClose, onDone }) {
  const [territory, setTerritory] = useState("");
  const [saving, setSaving] = useState(false);
  const overCap = businessIds.length > 25;
  const canApply = !!territory && !overCap && businessIds.length > 0 && !saving;

  // Phase 3I.4P.3.2 — reset every choice whenever the modal is closed OR
  // (re)opened. Cancel/close discards the temporary selection. Success
  // opens fresh with no stale tick.
  React.useEffect(() => {
    if (!open) { setTerritory(""); setSaving(false); return; }
    setTerritory(""); setSaving(false);
  }, [open]);

  if (!open) return null;

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await hqApi.post("/control/clients/bulk-territory", {
        business_ids: businessIds, territory,
      });
      // Phase 3I.4P.3.1 — verify the backend actually matched rows before
      // showing success. `matched_count` is the number of clients whose
      // canonical record was touched; `modified_count` is the subset that
      // actually changed value (e.g. was not already this territory).
      const matched  = Number(data.matched_count ?? data.updated ?? 0);
      const modified = Number(data.modified_count ?? matched);
      if (matched <= 0) {
        toast.error("No clients were updated. Please try again.");
        return;
      }
      const label = territoryLabel(territory);
      toast.success(
        modified === matched
          ? `Territory set to ${label} · ${matched} client${matched === 1 ? "" : "s"} updated`
          : `Territory set to ${label} · ${modified} updated, ${matched - modified} already ${label}`
      );
      onDone?.(data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Bulk territory update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="bulk-territory-modal">
      <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">Bulk Update</div>
            <div className="font-black text-lg">Set territory · {businessIds.length} client{businessIds.length === 1 ? "" : "s"}</div>
          </div>
          <button onClick={onClose} data-testid="bulk-territory-close" className="p-1 rounded hover:bg-[#F6F5F2]"><X size={18}/></button>
        </div>
        <div className="p-6 space-y-4">
          {overCap && (
            <div className="p-3 rounded-lg bg-[#FDECEC] text-[#C1350B] text-sm border border-[#F5C6C6]">
              Maximum 25 clients per bulk action. Reduce the selection first.
            </div>
          )}
          <label className="block">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[#8A8A8A] mb-1">Territory</div>
            <select
              value={territory}
              onChange={(e) => setTerritory(e.target.value)}
              data-testid="bulk-territory-select"
              className="w-full px-3 py-2 rounded-lg border"
            >
              <option value="">— Select territory —</option>
              {TERRITORY_OPTIONS.filter((t) => t.value).map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </label>
          <div className="text-[12px] text-[#8A8A8A]">
            Only the Territory field on the selected clients will change. Orange Score, services,
            pitch history, subscriptions, invoices and agreements are never touched.
          </div>
        </div>
        <div className="px-6 py-4 border-t border-[#EAEAEA] flex justify-end gap-2">
          <button onClick={onClose} data-testid="bulk-territory-cancel"
            className="px-4 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase">
            Cancel
          </button>
          <button onClick={apply} disabled={!canApply} data-testid="bulk-territory-apply"
            className={`px-5 py-2 rounded-full text-[11px] font-black tracking-[0.16em] uppercase ${canApply ? "bg-[#FF6A00] text-black hover:bg-[#e75f00]" : "bg-[#EAEAEA] text-[#8A8A8A] cursor-not-allowed"}`}>
            {saving ? "Applying…" : `Apply to ${businessIds.length} client${businessIds.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
